/* RESPONSIBLE TEAM: team-integration-apps */
import Model, { attr } from '@ember-data/model';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';

export type ImportOperationStatus =
  | 'pending'
  | 'shopify_exporting'
  | 'intercom_processing'
  | 'completed'
  | 'shopify_failed'
  | 'intercom_failed'
  | 'cancelled';

export interface ShopifyImportOperationData {
  status: ImportOperationStatus;
  progress_percentage: number;
  object_count: number;
  processed_count: number;
  failed_count: number;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
}

export interface ShopifyImportStatus {
  customers: ShopifyImportOperationData | null;
  orders: ShopifyImportOperationData | null;
}

const ACTIVE_IMPORT_STATUSES: ImportOperationStatus[] = [
  'pending',
  'shopify_exporting',
  'intercom_processing',
];

const FAILED_IMPORT_STATUSES: ImportOperationStatus[] = ['shopify_failed', 'intercom_failed'];

export const STORE_INSTALLATION_STATES: Record<string, string> = {
  ok: 'connected',
  down: 'disconnected',
  invalid_auth: 'invalid_auth',
  unknown: 'unknown',
};

export const STORE_INSTALLATION_STATES_STAMP_COLOR: Record<string, string> = {
  connected: 'green',
  disconnected: 'red',
  invalid_auth: 'yellow',
  unknown: 'gray',
};

export default class ShopifyIntegration extends Model {
  @service intl!: IntlService;

  @attr('string') declare intercom_app_id: string;
  @attr('string') declare shopId: string;
  @attr('string') declare shopifyDomain: string;
  @attr('string') declare installationStatus: string;
  @attr() declare importStatus: ShopifyImportStatus | null;
  @attr('boolean') declare partialRefundsEnabled: boolean | null;
  @attr('boolean') declare replacementOrdersEnabled: boolean | null;
  @attr('boolean') declare giftCardCompensationEnabled: boolean | null;
  @attr('boolean') declare replacementItemsEnabled: boolean | null;
  @attr('boolean') declare perItemRefundReasonEnabled: boolean | null;

  get stampLabel() {
    return this.intl.t(
      `appstore.settings.shopify.connect-shopify.status-label.${this.getStoreStatus()}`,
    );
  }

  get stampColor() {
    let stampColor = STORE_INSTALLATION_STATES_STAMP_COLOR[this.getStoreStatus()];
    return stampColor || 'gray';
  }

  getStoreStatus() {
    if (!this.installationStatus) {
      return 'unknown';
    }

    return STORE_INSTALLATION_STATES[this.installationStatus.toLowerCase()] || 'unknown';
  }

  private get importStatusWithDefault(): ShopifyImportStatus {
    return this.importStatus || { customers: null, orders: null };
  }

  get hasImportStatus(): boolean {
    let { customers, orders } = this.importStatusWithDefault;
    return customers !== null || orders !== null;
  }

  get hasActiveImport(): boolean {
    let { customers, orders } = this.importStatusWithDefault;
    return (
      (customers !== null && ACTIVE_IMPORT_STATUSES.includes(customers.status)) ||
      (orders !== null && ACTIVE_IMPORT_STATUSES.includes(orders.status))
    );
  }

  get hasCompletedImport(): boolean {
    let { customers, orders } = this.importStatusWithDefault;
    return (
      (customers !== null && customers.status === 'completed') ||
      (orders !== null && orders.status === 'completed')
    );
  }

  get hasFailedImport(): boolean {
    let { customers, orders } = this.importStatusWithDefault;
    return (
      (customers !== null && FAILED_IMPORT_STATUSES.includes(customers.status)) ||
      (orders !== null && FAILED_IMPORT_STATUSES.includes(orders.status))
    );
  }

  get customerImport(): ShopifyImportOperationData | null {
    return this.importStatusWithDefault.customers;
  }

  get orderImport(): ShopifyImportOperationData | null {
    return this.importStatusWithDefault.orders;
  }

  get customerImportIcon(): string {
    return this.customerImport ? this.operationIcon(this.customerImport) : '';
  }

  get orderImportIcon(): string {
    return this.orderImport ? this.operationIcon(this.orderImport) : '';
  }

  get customerImportStampColor(): string {
    return this.customerImport ? this.operationStampColor(this.customerImport) : '';
  }

  get orderImportStampColor(): string {
    return this.orderImport ? this.operationStampColor(this.orderImport) : '';
  }

  get customerImportTooltip(): string {
    return this.customerImport ? this.operationTooltip(this.customerImport) : '';
  }

  get orderImportTooltip(): string {
    return this.orderImport ? this.operationTooltip(this.orderImport) : '';
  }

  private operationIcon(op: ShopifyImportOperationData): string {
    if (ACTIVE_IMPORT_STATUSES.includes(op.status)) {
      return 'sync';
    }
    if (FAILED_IMPORT_STATUSES.includes(op.status)) {
      return 'close';
    }
    if (op.status === 'completed') {
      return 'check';
    }
    return 'minus-circle';
  }

  private operationStampColor(op: ShopifyImportOperationData): string {
    if (ACTIVE_IMPORT_STATUSES.includes(op.status)) {
      return 'blue';
    }
    if (FAILED_IMPORT_STATUSES.includes(op.status)) {
      return 'red';
    }
    if (op.status === 'completed') {
      return 'green';
    }
    return 'gray';
  }

  private operationTooltip(op: ShopifyImportOperationData): string {
    if (op.status === 'pending' || op.status === 'shopify_exporting') {
      return this.intl.t('appstore.settings.shopify.import-status.tooltip.preparing');
    }
    if (op.status === 'cancelled') {
      return this.intl.t('appstore.settings.shopify.import-status.tooltip.cancelled');
    }
    return this.intl.t('appstore.settings.shopify.import-status.tooltip.imported', {
      processedCount: op.processed_count.toLocaleString(),
    });
  }

  get analyticsData() {
    return {
      object: 'shopify_integration',
      shopify_integration_id: this.id,
    };
  }
}
